/**
 * Qué hace la gente dentro de la app, más allá de las vistas de página (26-10-2026).
 *
 * Una alta de la prueba de un pasaporte estuvo 86 s en su resultado y se fue sin exportar: ¿copió el dato a
 * mano, buscó algo que no encontró o solo miró? Con las vistas no se sabía. Esto añade, con el pixel de
 * `track.js`, tres eventos:
 *
 *   ui_click  {label, kind, area, to}   clic en un botón o enlace (no en texto ni en zonas vacías)
 *   copy      {area, field, chars}      copiar con el ratón o Ctrl+C
 *   print     {area}                    imprimir la página
 *
 * Privacidad (CLAUDE.md, «solo métricas»): nunca el texto copiado ni valores de documentos. La etiqueta de un
 * clic es el `data-track` del elemento o, si no tiene, su texto visible SALVO dentro de `[data-track-private]`
 * (resultados e historial: el texto puede ser un nombre de archivo o un dato), donde solo vale `data-track`.
 * Además se quitan correos y dígitos. `area` = `data-track-area` más cercano; `field` = `data-track-field`
 * (el nombre del campo de la plantilla, no su valor).
 */
import { track, normalizePath } from "./track";

const MAX_EVENTS_PER_LOAD = 300;
let sent = 0;
let installed = false;

/** Etiqueta legible y sin datos personales: correos fuera, dígitos → #, espacios colapsados, ≤ 40. */
export function cleanLabel(text) {
  return String(text || "")
    .replace(/[^\s@]+@[^\s@]+/g, "")
    .replace(/\d/g, "#")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 40);
}

const attr = (el, name) => {
  const n = el && el.closest ? el.closest(`[${name}]`) : null;
  return n ? n.getAttribute(name) : undefined;
};

// Botón solo con icono (sin texto ni aria-label): el nombre del icono de lucide («icon:trash-2») o, si no es
// de lucide (react-icons no pone clase), «icon» y la zona de la página. Nunca es dato de un documento.
function iconLabel(el) {
  const svg = el.querySelector("svg");
  if (!svg) return "";
  const cls = String(svg.getAttribute("class") || "").match(/\blucide-([a-z0-9-]+)/);
  if (cls) return `icon:${cls[1]}`;
  const zone = el.closest("header, nav, aside, [role=dialog], footer");
  return zone ? `icon:${zone.getAttribute("role") || zone.tagName.toLowerCase()}` : "icon";
}

export function clickProps(target) {
  const el = target && target.closest ? target.closest("button, a, [role='button'], [data-track]") : null;
  if (!el) return null;
  const isPrivate = Boolean(el.closest("[data-track-private]"));
  const own = el.getAttribute("data-track");
  const label = own || (isPrivate ? "" : cleanLabel(el.getAttribute("aria-label") || el.getAttribute("title") || el.textContent));
  const props = {
    label: label || iconLabel(el) || "(sin etiqueta)",
    kind: el.tagName === "A" ? "link" : "button",
    area: attr(el, "data-track-area") || undefined,
  };
  const href = el.tagName === "A" ? el.getAttribute("href") : null;
  if (href) {
    // Destino del enlace: ruta interna normalizada o solo el dominio de uno externo (nunca la URL entera).
    if (href.startsWith("/")) props.to = normalizePath(href.split("?")[0]);
    else {
      try {
        props.to = new URL(href).hostname;
      } catch {
        // mailto:, tel:… → sin destino
      }
    }
  }
  return props;
}

const emit = (name, props) => {
  if (sent >= MAX_EVENTS_PER_LOAD) return;
  sent += 1;
  track(name, props);
};

export function installInteractionTracking() {
  if (installed || typeof document === "undefined") return;
  installed = true;
  document.addEventListener(
    "click",
    (e) => {
      try {
        const p = clickProps(e.target);
        if (p) emit("ui_click", p);
      } catch {
        // una métrica nunca rompe la app
      }
    },
    true
  );
  document.addEventListener("copy", (e) => {
    try {
      const sel = window.getSelection ? String(window.getSelection() || "") : "";
      const from = e.target && e.target.nodeType === 1 ? e.target : e.target && e.target.parentElement;
      emit("copy", {
        area: attr(from, "data-track-area") || "page",
        field: attr(from, "data-track-field") ? String(attr(from, "data-track-field")).slice(0, 40) : undefined,
        chars: sel.length,
      });
    } catch {
      // idem
    }
  });
  window.addEventListener("beforeprint", () => emit("print", { area: "page" }));
}
